import { useLocation } from '@tanstack/react-router'
import { useQuery, type RefetchOptions } from '@tanstack/react-query'
import { useEffect, useMemo, type PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'

import {
  cacheBrandingSettings,
  defaultBrandingSettings,
  getPublicBrandingSettings,
  publicBrandingQueryKey,
  readCachedBrandingSettings,
  resolveBrandAssetSrc,
  resolveBrandingDocumentTitle,
} from './branding-core'
import { BrandingContext, type BrandingContextValue } from './branding-context'
import { useBranding } from './branding-hooks'

function updateFavicon(href: string) {
  const existingLinks = document.querySelectorAll<HTMLLinkElement>(
    'link[rel="icon"], link[rel="shortcut icon"]',
  )
  existingLinks.forEach((link) => link.remove())

  const link = document.createElement('link')
  link.rel = 'icon'
  link.href = href
  document.head.appendChild(link)
}

function updateDescriptionMeta(description: string) {
  const existingMeta = document.querySelector<HTMLMetaElement>('meta[name="description"]')

  if (!description) {
    existingMeta?.remove()
    return
  }

  if (existingMeta) {
    existingMeta.content = description
    return
  }

  const meta = document.createElement('meta')
  meta.name = 'description'
  meta.content = description
  document.head.appendChild(meta)
}

export function BrandingProvider({ children }: PropsWithChildren) {
  const brandingQuery = useQuery({
    initialData: () => readCachedBrandingSettings() ?? defaultBrandingSettings,
    initialDataUpdatedAt: 0,
    queryFn: getPublicBrandingSettings,
    queryKey: publicBrandingQueryKey,
    staleTime: 5 * 60 * 1000,
  })
  const { data: settings, isFetching, refetch } = brandingQuery

  useEffect(() => {
    cacheBrandingSettings(settings)
  }, [settings])

  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--color-chat-user-bubble', settings.chatUserBubbleColor)
    root.style.setProperty('--color-chat-user-bubble-text', settings.chatUserBubbleTextColor)

    return () => {
      root.style.removeProperty('--color-chat-user-bubble')
      root.style.removeProperty('--color-chat-user-bubble-text')
    }
  }, [settings.chatUserBubbleColor, settings.chatUserBubbleTextColor])

  const value = useMemo<BrandingContextValue>(
    () => ({
      isFetching,
      refresh: (options?: RefetchOptions) => refetch(options),
      settings,
    }),
    [isFetching, refetch, settings],
  )

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>
}

export function BrandingMetadataManager() {
  const location = useLocation()
  const { t } = useTranslation('shell')
  const {
    settings: { description, favicon, hubAgentName, title },
  } = useBranding()

  useEffect(() => {
    document.title = resolveBrandingDocumentTitle(location.pathname, title, hubAgentName, t)
    updateFavicon(resolveBrandAssetSrc(favicon))
    updateDescriptionMeta(description)
  }, [description, favicon, hubAgentName, location.pathname, t, title])

  return null
}
